import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { trackFilter, trackAction, trackModalOpen, trackDownload } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeftRight,
  Plus,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  Package,
  Trash2,
  Edit,
  RotateCcw,
  AlertTriangle,
  Lock,
  FileDown,
  Wifi,
  WifiOff,
  Search,
  ChevronDown,
  ChevronUp,
  X,
  Calendar,
  Handshake,
  Clock,
  Bell,
  Zap,
  ScanLine,
  Copy,
  Shield,
  Truck,
  PackageOpen,
  PackageCheck,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatShortDate, getMovementTypeColor, getMovementTypeLabel, downloadFile } from '../lib/utils';
import { MovementViewModal, MovementDeleteModal, MovementEditModal, ReturnModal, EmergencyModeModal, CreateMovementModal, WorkflowTransitionModal, RequestReturnConfirmModal } from '../components/movements';
import BarcodeScanner from '../components/BarcodeScanner';
import { SearchableSelect, MEXICAN_STATES, COUNTRIES } from '../components/SearchableSelect';
import ClientSuggestion from '../components/ClientSuggestion';

// Workflow status (4-step) styles for SALIDA + bomberazo movements
const WORKFLOW_BADGE_STYLES = {
  salida:     'bg-amber-500/20 text-amber-400 border-amber-500/30',
  entregado:  'bg-blue-500/20 text-blue-400 border-blue-500/30',
  retorno:    'bg-purple-500/20 text-purple-400 border-purple-500/30',
  completado: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};
const WORKFLOW_BADGE_LABELS = {
  salida: 'Salida',
  entregado: 'Entregado',
  retorno: 'Retorno',
  completado: 'Completado',
};

// Map any legacy 'verificado' status to 'completado' on the fly
const normalizeStatus = (s) => (s === 'verificado' ? 'completado' : s);

const MovementsPage = () => {
  const { api, user, isAdmin, hasPermission } = useAuth();
  const { isConnected, lastMessage } = useWebSocket();
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState([]);
  const [pendingSalidas, setPendingSalidas] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [locationsWithAvailability, setLocationsWithAvailability] = useState([]);
  const [categoriesWithAvailability, setCategoriesWithAvailability] = useState([]);
  
  // UI state
  const [showPendingSalidas, setShowPendingSalidas] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dismissedSalidas, setDismissedSalidas] = useState(() => {
    // Load dismissed salidas from localStorage
    const saved = localStorage.getItem('dismissedSalidas');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Save dismissed salidas to localStorage when changed
  useEffect(() => {
    localStorage.setItem('dismissedSalidas', JSON.stringify(dismissedSalidas));
  }, [dismissedSalidas]);
  
  // Filter out dismissed salidas
  const visiblePendingSalidas = pendingSalidas.filter(s => !dismissedSalidas.includes(s.id));
  
  const handleDismissSalida = (salidaId) => {
    setDismissedSalidas(prev => [...prev, salidaId]);
  };
  
  const handleRestoreDismissed = () => {
    setDismissedSalidas([]);
    toast.success('Salidas ocultas restauradas');
  };
  
  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  // Duplicate admin auth
  const [duplicateAuthRequired, setDuplicateAuthRequired] = useState(false);
  const [showDuplicateAuth, setShowDuplicateAuth] = useState(false);
  const [duplicateAuthCreds, setDuplicateAuthCreds] = useState({ username: '', password: '' });
  const [pendingDuplicate, setPendingDuplicate] = useState(null);
  
  // Emergency mode form - Same as normal but without password verification
  const [emergencyForm, setEmergencyForm] = useState({
    responsible_id: '',
    event_name: '',
    event_date: new Date().toISOString().split('T')[0],
    event_time: new Date().toTimeString().slice(0, 5),
    client_name: '',
    client_contact: '',
    client_id: '',
    control_number: '',
    event_city: 'CDMX',
    event_state: 'Ciudad de Mexico',
    event_country: 'Mexico',
    location_id: '',
    category_id: '',
    items: [],
    notes: ''
  });
  const [emergencyAvailableEquipment, setEmergencyAvailableEquipment] = useState([]);
  const [emergencyCategoriesWithAvailability, setEmergencyCategoriesWithAvailability] = useState([]);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  
  // Barcode scanner states
  const [showScanner, setShowScanner] = useState(false);
  const [scannerConfig, setScannerConfig] = useState({ enabled: false });
  const [scanMode, setScanMode] = useState('equipment'); // 'equipment', 'location'
  const [scanBatchMode, setScanBatchMode] = useState(false);
  
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [selectedSalida, setSelectedSalida] = useState(null);

  // Workflow transition modal state (SALIDA -> ENTREGADO -> RETORNO -> VERIFICADO)
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowAction, setWorkflowAction] = useState(null); // 'deliver' | 'return' | 'verify'
  const [returnReqOpen, setReturnReqOpen] = useState(false);
  const [returnReqMovement, setReturnReqMovement] = useState(null);
  const [workflowMovement, setWorkflowMovement] = useState(null);
  
  // Bulk selection state
  const [selectedMovements, setSelectedMovements] = useState(new Set());
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteCreds, setBulkDeleteCreds] = useState({ admin_username: '', admin_password: '' });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showResponsiblePassword, setShowResponsiblePassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  
  // Search mode toggles for unit selection
  const [searchBySerial, setSearchBySerial] = useState(true);
  const [searchByReceptor, setSearchByReceptor] = useState(false);
  
  // Movement form - Updated with new fields including loan
  const [form, setForm] = useState({
    type: 'salida',
    movement_subtype: 'evento', // 'evento' or 'prestamo'
    responsible_id: '',
    receiver_user_id: '',
    event_date: new Date().toISOString().split('T')[0],
    event_time: '',
    event_name: '',
    client_name: '',
    client_contact: '',
    client_id: '',
    control_number: '',
    event_city: 'CDMX',
    event_state: 'Ciudad de Mexico',
    event_country: 'Mexico',
    user_password: '',
    notes: '',
    items: [],
    admin_override: false,
    admin_username: '',
    admin_password: '',
    salida_id: '',
    // Loan-specific fields
    loan_reason: '',
    loan_duration_value: '',
    loan_duration_unit: 'days',
    loan_indefinite: false,
    loan_reminders_enabled: false,
    loan_reminder_days: [3, 7],
    // Equipment state check
    equipment_state_checked: false,
    equipment_no_review: false,  // For emergency/bomberazo cases
    include_qr: false  // Include QR code for client digital signature in PDF
  });

  // New item - Updated with location
  const [newItem, setNewItem] = useState({
    location_id: '',
    category_id: '',  // Filter by category
    equipment_id: '',
    quantity: 1,
    condition: 'bueno',
    fault_reason: '',
    serial_numbers: '',
    selected_units: []  // For selecting specific serial numbers
  });

  // State for available equipment at selected location
  const [availableAtLocation, setAvailableAtLocation] = useState([]);

  // Get available quantity for selected equipment at selected location
  const getAvailableQuantity = (equipmentId, locationId) => {
    const eq = equipment.find(e => e.id === equipmentId);
    if (!eq || !eq.locations) return 0;
    const loc = eq.locations.find(l => l.location_id === locationId);
    return loc ? loc.available : 0;
  };

  // Load available equipment when location changes
  const loadAvailableEquipment = async (locationId) => {
    if (!locationId) {
      setAvailableAtLocation([]);
      setCategoriesWithAvailability([]);
      return;
    }
    try {
      const [equipmentRes, categoriesRes] = await Promise.all([
        api().get(`/locations/${locationId}/available-equipment`),
        api().get(`/categories/with-availability?location_id=${locationId}`)
      ]);
      setAvailableAtLocation(equipmentRes.data);
      setCategoriesWithAvailability(categoriesRes.data || []);
    } catch (error) {
      console.error('Error loading available equipment:', error);
      setAvailableAtLocation([]);
      setCategoriesWithAvailability([]);
    }
  };

  // Filter available equipment by category
  const filteredAvailableEquipment = newItem.category_id 
    ? availableAtLocation.filter(eq => {
        const fullEquipment = equipment.find(e => e.id === eq.equipment_id);
        return fullEquipment?.category_id === newItem.category_id;
      })
    : availableAtLocation;

  // Compute reserved unit IDs (already added to form.items) to prevent duplicates
  const reservedUnitSerials = new Set(
    form.items.flatMap(item => 
      (item.serial_numbers || '').split(',').map(s => s.trim()).filter(Boolean)
    )
  );

  useEffect(() => {
    loadData();
    // Load duplicate settings
    api().get('/duplicate-settings').then(res => {
      setDuplicateAuthRequired(res.data?.require_admin_auth || false);
    }).catch(() => {});
  }, []);

  // v5.3.6: Deep-link handler — opens the view modal when URL has ?open=<movementId>.
  // Fires after movements are loaded so the modal receives the full record.
  useEffect(() => {
    if (loading) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const openId = params.get('open');
      const confirmId = params.get('confirm');
      if (confirmId) {
        const target = movements.find(m => m.id === confirmId);
        if (target) {
          const stage = target.pending_confirmation?.stage;
          let action = null;
          if (stage === 'receive' || (target.receiver_user_id && !target.receipt_info)) action = 'receipt';
          else if (normalizeStatus(target.status || target.workflow_status) === 'retorno' && !target.return_receipt_info) action = 'return_receipt';
          if (action) {
            setWorkflowMovement(target);
            setWorkflowAction(action);
            setWorkflowModalOpen(true);
          } else {
            setSelectedMovement(target);
            setIsViewModalOpen(true);
          }
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        }
        return;
      }
      if (!openId) return;
      const target = movements.find(m => m.id === openId);
      if (target) {
        setSelectedMovement(target);
        setIsViewModalOpen(true);
        // Clean the URL so a manual refresh doesn't keep popping the modal
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', cleanUrl);
      }
    } catch (_err) { /* no-op */ }
    // We want this to run whenever the loaded list changes (initial load + reloads).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, movements]);

  // WebSocket real-time updates
  useEffect(() => {
    if (lastMessage) {
      const refreshTypes = ['movement_created', 'movement_updated', 'movement_deleted'];
      if (refreshTypes.includes(lastMessage.type)) {
        loadData();
      }
    }
  }, [lastMessage]);

  const [systemUsers, setSystemUsers] = useState([]);
  const loadData = async () => {
    setLoading(true);
    try {
      const apiInstance = api();
      const [movementsRes, responsiblesRes, equipmentRes, locationsRes, pendingRes, categoriesRes, scannerConfigRes, locationsWithAvailRes, clientsRes, usersRes] = await Promise.all([
        apiInstance.get('/movements'),
        apiInstance.get('/responsibles'),
        apiInstance.get('/equipment'),
        apiInstance.get('/locations'),
        apiInstance.get('/movements/salidas-pendientes'),
        apiInstance.get('/categories'),
        apiInstance.get('/barcode/config').catch(() => ({ data: { enabled: false } })),
        apiInstance.get('/locations/with-availability').catch(() => ({ data: [] })),
        apiInstance.get('/clients').catch(() => ({ data: [] })),
        apiInstance.get('/users').catch(() => ({ data: [] }))
      ]);
      setMovements(movementsRes.data);
      setResponsibles(responsiblesRes.data.filter(r => r.status === 'active'));
      setEquipment(equipmentRes.data);
      setLocations(locationsRes.data);
      setPendingSalidas(pendingRes.data);
      setCategories(categoriesRes.data);
      setScannerConfig(scannerConfigRes.data || { enabled: false });
      // Store locations with availability for movement creation
      setLocationsWithAvailability(locationsWithAvailRes.data || []);
      setClients(clientsRes.data || []);
      const _usersData = usersRes.data;
      const _usersList = Array.isArray(_usersData) ? _usersData : (_usersData.users || _usersData.items || []);
      setSystemUsers(_usersList.filter(u => (u.status || 'active') === 'active'));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Handle scanned entity from barcode scanner
  const handleScanSuccess = (scanResult) => {
    if (scanResult.type === 'location') {
      // Set location in form
      setNewItem(prev => ({
        ...prev,
        location_id: scanResult.entity.id
      }));
      toast.success(`Ubicación seleccionada: ${scanResult.entity.name}`);
    } else if (scanResult.type === 'equipment') {
      // Add equipment type - user still needs to select quantity
      const eq = scanResult.entity;
      setNewItem(prev => ({
        ...prev,
        equipment_id: eq.id
      }));
      toast.success(`Equipo seleccionado: ${eq.name}`);
    } else if (scanResult.type === 'unit') {
      // Add specific unit directly
      const unit = scanResult.entity;
      const parentEquipment = scanResult.equipment;
      
      if (unit.status !== 'disponible') {
        toast.error(`La unidad ${unit.serial_number} no está disponible (${unit.status})`);
        return;
      }
      
      // Check if already in items
      const existingItem = form.items.find(i => 
        i.equipment_id === unit.equipment_id && 
        i.selected_units?.includes(unit.id)
      );
      
      if (existingItem) {
        toast.warning('Esta unidad ya está en la lista');
        return;
      }
      
      // Add as new item with this specific unit
      const newItemToAdd = {
        equipment_id: unit.equipment_id,
        equipment_name: parentEquipment?.name || 'Equipo',
        quantity: 1,
        condition: 'bueno',
        fault_reason: '',
        location_id: unit.location_id,
        location_name: unit.location_name,
        selected_units: [unit.id],
        serial_numbers: unit.serial_number,
        receptor_numbers: unit.receptor_number || ''
      };
      
      setForm(prev => ({
        ...prev,
        items: [...prev.items, newItemToAdd]
      }));
      
      toast.success(`Unidad agregada: ${unit.serial_number}`);
    }
    
    setShowScanner(false);
  };

  // Handle batch scan results
  const handleBatchScanSuccess = (batchResults) => {
    let addedCount = 0;
    
    batchResults.forEach(scanResult => {
      if (scanResult.type === 'unit') {
        const unit = scanResult.entity;
        const parentEquipment = scanResult.equipment;
        
        if (unit.status !== 'disponible') return;
        
        const existingItem = form.items.find(i => 
          i.equipment_id === unit.equipment_id && 
          i.selected_units?.includes(unit.id)
        );
        if (existingItem) return;
        
        const newItemToAdd = {
          equipment_id: unit.equipment_id,
          equipment_name: parentEquipment?.name || 'Equipo',
          quantity: 1,
          condition: 'bueno',
          fault_reason: '',
          location_id: unit.location_id,
          location_name: unit.location_name,
          selected_units: [unit.id],
          serial_numbers: unit.serial_number,
          receptor_numbers: unit.receptor_number || ''
        };
        
        setForm(prev => ({
          ...prev,
          items: [...prev.items, newItemToAdd]
        }));
        addedCount++;
      } else if (scanResult.type === 'equipment') {
        const eq = scanResult.entity;
        setNewItem(prev => ({ ...prev, equipment_id: eq.id }));
        addedCount++;
      }
    });
    
    if (addedCount > 0) {
      toast.success(`${addedCount} elemento(s) agregados del escaneo múltiple`);
    }
    setShowScanner(false);
  };

  // Filter movements by search term (v5.3.5: now includes transition notes from
  // delivery_info, return_info y verification_info, además de mov.notes original)
  const filteredMovements = movements.filter(mov => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      mov.responsible_name?.toLowerCase().includes(search) ||
      mov.event_name?.toLowerCase().includes(search) ||
      mov.client_name?.toLowerCase().includes(search) ||
      mov.notes?.toLowerCase().includes(search) ||
      mov.delivery_info?.notes?.toLowerCase().includes(search) ||
      mov.return_info?.notes?.toLowerCase().includes(search) ||
      mov.verification_info?.notes?.toLowerCase().includes(search) ||
      mov.type?.toLowerCase().includes(search) ||
      mov.items?.some(item =>
        item.equipment_name?.toLowerCase().includes(search) ||
        item.serial_numbers?.toLowerCase().includes(search) ||
        item.receptor_numbers?.toLowerCase().includes(search)
      )
    );
  });

  // Bulk selection helpers
  const isAllSelected = filteredMovements.length > 0 && filteredMovements.every(m => selectedMovements.has(m.id));
  const isSomeSelected = selectedMovements.size > 0;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedMovements(new Set());
    } else {
      setSelectedMovements(new Set(filteredMovements.map(m => m.id)));
    }
  };

  const toggleSelectMovement = (movementId) => {
    setSelectedMovements(prev => {
      const next = new Set(prev);
      if (next.has(movementId)) {
        next.delete(movementId);
      } else {
        next.add(movementId);
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!bulkDeleteCreds.admin_username || !bulkDeleteCreds.admin_password) {
      toast.error('Ingresa las credenciales de administrador');
      return;
    }
    setBulkDeleting(true);
    try {
      const apiInstance = api();
      const res = await apiInstance.post('/movements/bulk-delete', {
        movement_ids: Array.from(selectedMovements),
        admin_username: bulkDeleteCreds.admin_username,
        admin_password: bulkDeleteCreds.admin_password,
      });
      toast.success(res.data.message || `${res.data.deleted} movimiento(s) eliminado(s)`);
      setSelectedMovements(new Set());
      setIsBulkDeleteModalOpen(false);
      setBulkDeleteCreds({ admin_username: '', admin_password: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar movimientos');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openCreateModal = (type = 'salida', salida = null) => {
    setForm({
      type,
      movement_subtype: 'evento', // Default to 'evento' tab
      responsible_id: salida?.responsible_id || '',
      event_date: new Date().toISOString().split('T')[0],
      event_time: '',
      event_name: '',
      client_name: '',
      client_contact: '',
      control_number: '',
      event_city: 'CDMX',
      event_state: 'Ciudad de Mexico',
      event_country: 'Mexico',
      user_password: '',
      notes: '',
      items: [],
      admin_override: false,
      admin_username: '',
      admin_password: '',
      salida_id: salida?.id || '',
      // Loan fields - reset
      loan_reason: '',
      loan_duration_value: '',
      loan_duration_unit: 'days',
      loan_indefinite: false,
      loan_reminders_enabled: false,
      loan_reminder_days: [3, 7],
      // Equipment state
      equipment_state_checked: false,
      equipment_no_review: false,
      include_qr: false
    });
    setSelectedSalida(salida);
    setDuplicateInfo(null);
    setNewItem({ location_id: '', category_id: '', equipment_id: '', quantity: 1, condition: 'bueno', fault_reason: '', serial_numbers: '', selected_units: [], serial_search: '' });
    setAvailableAtLocation([]);
    setIsModalOpen(true);
  };

  const openDuplicateModal = (movement) => {
    // If admin auth required, show auth dialog first
    if (duplicateAuthRequired) {
      setPendingDuplicate(movement);
      setDuplicateAuthCreds({ username: '', password: '' });
      setShowDuplicateAuth(true);
      return;
    }
    executeDuplicate(movement);
  };

  const executeDuplicate = (movement) => {
    setDuplicateInfo({
      original_id: movement.id,
      original_event: movement.event_name || movement.loan_reason || 'Movimiento'
    });
    setForm({
      type: movement.type === 'prestamo' ? 'prestamo' : 'salida',
      movement_subtype: movement.type === 'prestamo' ? 'prestamo' : 'evento',
      responsible_id: movement.responsible_id || '',
      event_date: new Date().toISOString().split('T')[0],
      event_time: movement.event_time || '',
      event_name: movement.event_name || '',
      client_name: movement.client_name || '',
      client_contact: movement.client_contact || '',
      client_id: movement.client_id || '',
      control_number: '',
      event_city: movement.event_city || 'CDMX',
      event_state: movement.event_state || 'Ciudad de Mexico',
      event_country: movement.event_country || 'Mexico',
      user_password: '',
      notes: `[DUPLICADO] Copia de movimiento ${movement.id?.slice(0, 8)}`,
      items: [],
      admin_override: false,
      admin_username: '',
      admin_password: '',
      salida_id: '',
      loan_reason: movement.loan_reason || '',
      loan_duration_value: movement.loan_duration_value ? String(movement.loan_duration_value) : '',
      loan_duration_unit: movement.loan_duration_unit || 'days',
      loan_indefinite: movement.loan_duration_unit === 'indefinite',
      loan_reminders_enabled: false,
      loan_reminder_days: [3, 7],
      equipment_state_checked: false,
      equipment_no_review: false,
      include_qr: false,
      is_duplicate: true,
      duplicated_from: movement.id
    });
    setSelectedSalida(null);
    setNewItem({ location_id: '', category_id: '', equipment_id: '', quantity: 1, condition: 'bueno', fault_reason: '', serial_numbers: '', selected_units: [], serial_search: '' });
    setAvailableAtLocation([]);
    setIsModalOpen(true);
  };

  const handleDuplicateAuthConfirm = async () => {
    try {
      const apiInstance = api();
      await apiInstance.post('/auth/verify-admin', duplicateAuthCreds);
      setShowDuplicateAuth(false);
      if (pendingDuplicate) {
        executeDuplicate(pendingDuplicate);
        setPendingDuplicate(null);
      }
    } catch (error) {
      toast.error('Credenciales de admin invalidas');
    }
  };

  const handleExportCSV = async () => {
    try {
      const apiInstance = api();
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.responsible_id) params.append('responsible_id', filters.responsible_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      const response = await apiInstance.get(`/movements/export-csv?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `movimientos_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar CSV');
    }
  };

  const openReturnModal = (salida) => {
    setSelectedSalida(salida);
    // Pre-populate items from salida with partial return fields
    const returnItems = salida.items?.map(item => ({
      equipment_id: item.equipment_id,
      location_id: item.location_id,
      equipment_name: item.equipment_name,
      location_name: item.location_name,
      quantity: item.quantity,
      max_quantity: item.quantity,
      qty_good: item.quantity,  // Default all to good
      qty_faulty: 0,
      qty_lost: 0,
      condition: 'bueno',
      fault_reason: '',
      serial_numbers: '',
      receptor_numbers: item.receptor_numbers || '',
      available_serials: []  // Will be loaded
    })) || [];
    
    setForm({
      type: 'retorno',
      responsible_id: salida.responsible_id,
      event_date: salida.event_date || new Date().toISOString().split('T')[0],
      event_name: salida.event_name || '',
      client_name: salida.client_name || '',
      client_contact: salida.client_contact || '',
      control_number: salida.control_number || '',
      user_password: '',
      notes: '',
      items: returnItems,
      admin_override: false,
      admin_username: '',
      admin_password: '',
      salida_id: salida.id
    });
    
    // Load assigned units for this movement
    loadAssignedUnits(salida.id, returnItems);
    setIsReturnModalOpen(true);
  };

  // Fase 2 — notifica al usuario que entrega al almacén para que confirme el retorno
  const submitReturnRequest = async (warehouseUserId) => {
    try {
      const apiInstance = api();
      await apiInstance.post(`/movements/${returnReqMovement.id}/request-return-confirmation`, { warehouse_user_id: warehouseUserId });
      toast.success('Notificación enviada. El usuario debe confirmar la entrega al almacén con su usuario y contraseña.');
      setReturnReqOpen(false);
      setReturnReqMovement(null);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo solicitar la confirmación de retorno');
    }
  };


  const loadAssignedUnits = async (movementId, items) => {
    try {
      const response = await api().get(`/movements/${movementId}/assigned-units`);
      const units = response.data;
      
      // Group units by equipment_id
      const unitsByEquipment = {};
      units.forEach(unit => {
        if (!unitsByEquipment[unit.equipment_id]) {
          unitsByEquipment[unit.equipment_id] = [];
        }
        unitsByEquipment[unit.equipment_id].push(unit);
      });
      
      // Update items with available serials
      const updatedItems = items.map(item => ({
        ...item,
        available_serials: unitsByEquipment[item.equipment_id] || []
      }));
      
      setForm(prev => ({ ...prev, items: updatedItems }));
    } catch (error) {
      console.error('Error loading assigned units:', error);
    }
  };

  const openViewModal = (movement) => {
    setSelectedMovement(movement);
    setIsViewModalOpen(true);
  };

  const openDeleteModal = (movement) => {
    setSelectedMovement(movement);
    setIsDeleteModalOpen(true);
  };

  const openEditModal = (movement) => {
    setSelectedMovement(movement);
    setIsEditModalOpen(true);
  };

  const handleModalSuccess = () => {
    loadData();
  };

  const addItem = () => {
    if (!newItem.location_id || !newItem.equipment_id || newItem.quantity < 1) {
      toast.error('Selecciona ubicación, equipo y cantidad válida');
      return;
    }

    const equipmentData = availableAtLocation.find(e => e.equipment_id === newItem.equipment_id);
    const locationName = locations.find(l => l.id === newItem.location_id)?.name || '';
    
    // Check available quantity at this location
    const available = equipmentData?.available_count || 0;
    
    // Validate availability for salidas
    if (form.type === 'salida') {
      if (!equipmentData || available <= 0) {
        toast.error(`No hay inventario disponible de este equipo en esta ubicación`);
        return;
      }
      if (newItem.quantity > available) {
        toast.error(`Stock insuficiente. Disponible: ${available}, Solicitado: ${newItem.quantity}`);
        return;
      }
    }

    // Validate fault reason
    if ((newItem.condition === 'falla' || newItem.condition === 'faltante' || newItem.condition === 'perdido') && (!newItem.fault_reason || newItem.fault_reason.length < 5)) {
      toast.error('Debes agregar una razón (mínimo 5 caracteres) para equipos con falla, faltantes o perdidos');
      return;
    }

    // Build serial numbers string from selected units
    const serialNumbers = newItem.selected_units.length > 0 
      ? newItem.selected_units.map(u => u.serial_number).join(', ')
      : newItem.serial_numbers;

    // Build receptor numbers string from selected units
    const receptorNumbers = newItem.selected_units.length > 0
      ? newItem.selected_units.map(u => u.receptor_number).filter(Boolean).join(', ')
      : '';

    setForm({
      ...form,
      items: [...form.items, { 
        equipment_id: newItem.equipment_id,
        location_id: newItem.location_id,
        equipment_name: equipmentData?.equipment_name,
        equipment_status: equipmentData?.equipment_status || 'disponible',  // Include equipment status
        location_name: locationName,
        quantity: newItem.quantity,
        condition: newItem.condition,
        fault_reason: newItem.fault_reason,
        serial_numbers: serialNumbers,
        receptor_numbers: receptorNumbers
      }]
    });
    setNewItem({ location_id: '', category_id: '', equipment_id: '', quantity: 1, condition: 'bueno', fault_reason: '', serial_numbers: '', selected_units: [], serial_search: '' });
    setAvailableAtLocation([]);
  };

  const removeItem = (index) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index)
    });
  };

  const updateReturnItem = (index, field, value) => {
    const newItems = [...form.items];
    const item = newItems[index];
    newItems[index] = { ...item, [field]: value };
    
    // Clear fault reason if condition is not falla, faltante, or perdido
    if (field === 'condition' && !['falla', 'faltante', 'perdido'].includes(value)) {
      newItems[index].fault_reason = '';
      newItems[index].serial_numbers = '';
    }
    
    // Auto-adjust qty_good when qty_faulty or qty_lost changes to maintain sum = max_quantity
    if (field === 'qty_faulty' || field === 'qty_lost') {
      const maxQty = item.max_quantity;
      const faulty = field === 'qty_faulty' ? value : (newItems[index].qty_faulty || 0);
      const lost = field === 'qty_lost' ? value : (newItems[index].qty_lost || 0);
      // Calculate remaining for good items
      const remaining = maxQty - faulty - lost;
      newItems[index].qty_good = Math.max(0, remaining);
      // Clear per-condition serial selections when their count reaches 0
      if (field === 'qty_faulty' && value === 0) {
        newItems[index].faulty_serials = '';
        newItems[index].faulty_serial_search = '';
      }
      if (field === 'qty_lost' && value === 0) {
        newItems[index].lost_serials = '';
        newItems[index].lost_serial_search = '';
      }
    }
    
    setForm({ ...form, items: newItems });
  };

  const handleSave = async () => {
    if (!form.responsible_id) {
      toast.error('Selecciona un responsable');
      return;
    }
    
    // Validate based on movement subtype
    if (form.movement_subtype === 'evento') {
      if (!form.event_date || !form.client_name) {
        toast.error('Fecha y cliente son obligatorios');
        return;
      }
      const cn = (form.control_number || '').trim();
      if (!(cn.length === 5 && /^\d{5}$/.test(cn))) {
        toast.error('El No. de Control debe tener exactamente 5 dígitos numéricos');
        return;
      }
    } else if (form.movement_subtype === 'prestamo') {
      if (!form.loan_reason) {
        toast.error('El motivo del préstamo es obligatorio');
        return;
      }
      if (!form.loan_indefinite && !form.loan_duration_value) {
        toast.error('Especifica la duración del préstamo o marca "Sin tiempo determinado"');
        return;
      }
    }
    
    if (form.items.length === 0) {
      toast.error('Agrega al menos un equipo');
      return;
    }

    // Validate equipment state check for checkouts (salidas and loans)
    // Must have either equipment_state_checked OR equipment_no_review
    if ((form.type === 'salida' || form.movement_subtype === 'prestamo') && form.items.length > 0 && !form.equipment_state_checked && !form.equipment_no_review) {
      toast.error('Debes confirmar la revisión del equipo o indicar que sale sin revisión');
      return;
    }

    // Validate authentication
    if (!form.admin_override && !form.user_password) {
      toast.error('Ingresa la contraseña del responsable o usa override de administrador');
      return;
    }
    if (form.admin_override && (!form.admin_username || !form.admin_password)) {
      toast.error('Ingresa las credenciales de administrador para el override');
      return;
    }

    // Validate fault reasons for retornos with partial conditions
    if (form.type === 'retorno') {
      for (const item of form.items) {
        // Check that sum equals max_quantity
        const qtyGood = item.qty_good ?? item.max_quantity;
        const qtyFaulty = item.qty_faulty || 0;
        const qtyLost = item.qty_lost || 0;
        const total = qtyGood + qtyFaulty + qtyLost;
        
        if (total !== item.max_quantity) {
          toast.error(`La suma de equipos para "${item.equipment_name}" (${total}) debe ser igual a ${item.max_quantity}`);
          return;
        }
        
        // Require fault reason if there are faulty or lost items
        if ((qtyFaulty > 0 || qtyLost > 0) && (!item.fault_reason || item.fault_reason.length < 5)) {
          toast.error(`Debes agregar una razón para el equipo "${item.equipment_name}" con fallas o perdidos`);
          return;
        }

        // Require explicit serial selection per condition (or 'No identificado')
        const _hasEnough = (raw, expected) => {
          if (expected <= 0) return true;
          const s = (raw || '').trim();
          if (s.includes('No identificado')) return true;
          const arr = s.split(',').map(x => x.trim()).filter(Boolean);
          return arr.length >= expected;
        };
        if (qtyFaulty > 0 && (item.available_serials?.length || 0) > 0 && !_hasEnough(item.faulty_serials, qtyFaulty)) {
          toast.error(`Selecciona ${qtyFaulty} unidad(es) CON FALLA para "${item.equipment_name}" (o marca No identificado)`);
          return;
        }
        if (qtyLost > 0 && (item.available_serials?.length || 0) > 0 && !_hasEnough(item.lost_serials, qtyLost)) {
          toast.error(`Selecciona ${qtyLost} unidad(es) PERDIDA(S) para "${item.equipment_name}" (o marca No identificado)`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      const apiInstance = api();
      
      // Build items array - for returns, split into multiple items based on condition
      let processedItems = [];
      
      if (form.type === 'retorno') {
        for (const item of form.items) {
          const qtyGood = item.qty_good ?? item.max_quantity;
          const qtyFaulty = item.qty_faulty || 0;
          const qtyLost = item.qty_lost || 0;
          
          // v5.3.0: explicit per-condition serial selection
          const parseSerials = (s) => (s || '')
            .split(',').map(x => x.trim()).filter(Boolean);
          const isNoIdFaulty = (item.faulty_serials || '').includes('No identificado');
          const isNoIdLost = (item.lost_serials || '').includes('No identificado');
          const faultySerialsArr = parseSerials(item.faulty_serials).filter(s => s !== 'No identificado');
          const lostSerialsArr = parseSerials(item.lost_serials).filter(s => s !== 'No identificado');

          // Backwards-compat: if only legacy `serial_numbers` is filled, split it
          if (faultySerialsArr.length === 0 && lostSerialsArr.length === 0 && (item.serial_numbers || '')) {
            const legacy = parseSerials(item.serial_numbers).filter(s => s !== 'No identificado');
            faultySerialsArr.push(...legacy.slice(0, qtyFaulty));
            lostSerialsArr.push(...legacy.slice(qtyFaulty, qtyFaulty + qtyLost));
          }
          
          // Add good items (no serials needed for good items)
          if (qtyGood > 0) {
            processedItems.push({
              equipment_id: item.equipment_id,
              location_id: item.location_id || null,
              quantity: qtyGood,
              condition: 'bueno',
              fault_reason: null,
              serial_numbers: null
            });
          }
          
          // Add faulty items with their explicitly-selected serials
          if (qtyFaulty > 0) {
            processedItems.push({
              equipment_id: item.equipment_id,
              location_id: item.location_id || null,
              quantity: qtyFaulty,
              condition: 'falla',
              fault_reason: item.fault_reason || null,
              serial_numbers: faultySerialsArr.length > 0 ? faultySerialsArr.join(', ') : (isNoIdFaulty ? null : null)
            });
          }
          
          // Add lost items with their explicitly-selected serials
          if (qtyLost > 0) {
            processedItems.push({
              equipment_id: item.equipment_id,
              location_id: item.location_id || null,
              quantity: qtyLost,
              condition: 'perdido',
              fault_reason: item.fault_reason || null,
              serial_numbers: lostSerialsArr.length > 0 ? lostSerialsArr.join(', ') : (isNoIdLost ? null : null)
            });
          }
        }
      } else {
        // For salidas and prestamos, use items as-is
        processedItems = form.items.map(item => ({
          equipment_id: item.equipment_id,
          location_id: item.location_id || null,
          quantity: item.quantity,
          condition: item.condition || 'bueno',
          fault_reason: item.fault_reason || null,
          serial_numbers: item.serial_numbers || null,
          receptor_numbers: item.receptor_numbers || null
        }));
      }
      
      const movementData = {
        type: form.movement_subtype === 'prestamo' ? 'prestamo' : form.type,
        responsible_id: form.responsible_id,
        receiver_user_id: (form.type === 'salida' && form.movement_subtype === 'evento') ? (form.receiver_user_id || null) : null,
        event_date: form.movement_subtype === 'evento' ? form.event_date : null,
        event_time: form.movement_subtype === 'evento' ? (form.event_time || '') : null,
        event_name: form.movement_subtype === 'evento' ? form.event_name : null,
        client_name: form.movement_subtype === 'evento' ? form.client_name : null,
        client_contact: form.movement_subtype === 'evento' ? (form.client_contact || '') : null,
        client_id: form.client_id || null,
        control_number: form.movement_subtype === 'evento' ? (form.control_number || '') : null,
        event_city: form.event_city || 'CDMX',
        event_state: form.event_state || 'Ciudad de Mexico',
        event_country: form.event_country || 'Mexico',
        notes: form.notes,
        salida_id: form.salida_id || null,
        user_password: form.admin_override ? null : form.user_password,
        admin_override: form.admin_override,
        admin_username: form.admin_override ? form.admin_username : null,
        admin_password: form.admin_override ? form.admin_password : null,
        // Equipment review status
        equipment_reviewed: form.equipment_state_checked,
        equipment_no_review: form.equipment_no_review,
        // Digital signature QR (only relevant for salida/prestamo)
        include_qr: form.include_qr || false,
        // Loan-specific fields
        loan_reason: form.movement_subtype === 'prestamo' ? form.loan_reason : null,
        loan_duration_value: form.movement_subtype === 'prestamo' && !form.loan_indefinite ? parseInt(form.loan_duration_value) : null,
        loan_duration_unit: form.movement_subtype === 'prestamo' ? (form.loan_indefinite ? 'indefinite' : form.loan_duration_unit) : null,
        loan_reminders_enabled: form.movement_subtype === 'prestamo' ? form.loan_reminders_enabled : false,
        loan_reminder_days_before: form.movement_subtype === 'prestamo' && form.loan_reminders_enabled ? [3, 7] : null,
        items: processedItems,
        is_duplicate: form.is_duplicate || false,
        duplicated_from: form.duplicated_from || null
      };

      await apiInstance.post('/movements', movementData);

      const typeLabel = form.movement_subtype === 'prestamo' ? 'Préstamo' : (form.type === 'salida' ? 'Salida' : 'Retorno');
      toast.success(`${typeLabel} registrado exitosamente`);
      setIsModalOpen(false);
      setIsReturnModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear movimiento');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (movementId) => {
    try {
      const apiInstance = api();
      const response = await apiInstance.get(`/movements/${movementId}/pdf`, {
        responseType: 'blob'
      });
      downloadFile(response.data, `Responsiva_${movementId.substring(0, 8)}.pdf`);
      toast.success('PDF descargado');
    } catch (error) {
      console.error('PDF Error:', error);
      toast.error(error.response?.data?.detail || 'Error al generar PDF');
    }
  };

  const getConditionBadge = (condition) => {
    const styles = {
      bueno: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      regular: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      falla: 'bg-red-500/20 text-red-400 border-red-500/30',
      faltante: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      perdido: 'bg-red-600/20 text-red-400 border-red-600/30'
    };
    const labels = {
      bueno: 'Bueno',
      regular: 'Regular',
      falla: 'Con Falla',
      faltante: 'Faltante',
      perdido: 'Perdido'
    };
    return <Badge className={styles[condition] || styles.bueno}>{labels[condition] || condition}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="movements-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Movimientos</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            Registra salidas y retornos de equipo
            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${isConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {isConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {isConnected ? 'En vivo' : 'Offline'}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          {hasPermission('use_emergency_mode') && (
            <Button 
              variant="outline" 
              onClick={() => setShowEmergencyModal(true)} 
              className="border-red-500 text-red-500 hover:bg-red-500/10"
              data-testid="emergency-mode-btn"
            >
              <Zap className="h-4 w-4 mr-2" />
              Modo Emergencia
            </Button>
          )}
          <Button onClick={() => openCreateModal('salida')} className="btn-primary" data-testid="add-movement-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Salida
          </Button>
          <Button variant="outline" size="default" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Pending Salidas Alert */}
      {visiblePendingSalidas.length > 0 && (
        <Card className={`bg-amber-500/10 border-amber-500/30 transition-all ${!showPendingSalidas ? 'py-0' : ''}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-400">
                <AlertTriangle className="h-5 w-5" />
                Salidas Pendientes de Retorno ({visiblePendingSalidas.length})
                {dismissedSalidas.length > 0 && (
                  <span className="text-xs text-muted-foreground font-normal ml-2">
                    ({dismissedSalidas.length} ocultas)
                  </span>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                {dismissedSalidas.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRestoreDismissed}
                    className="text-muted-foreground hover:text-foreground text-xs"
                  >
                    Restaurar ocultas
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPendingSalidas(!showPendingSalidas)}
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                >
                  {showPendingSalidas ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-1" />
                      Ocultar
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      Mostrar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          {showPendingSalidas && (
            <CardContent>
              <div className="space-y-2">
                {visiblePendingSalidas.slice(0, 8).map((salida) => (
                  <div key={salida.id} className="flex items-center justify-between p-3 bg-background/50 rounded-lg group">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {salida.event_name || 'Sin nombre de evento'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {salida.responsible_name} • {formatDate(salida.event_date || salida.date)} • {salida.items?.length || 0} equipos
                        {salida.receiver_user_name && (
                          <span className="block text-xs text-muted-foreground">Recibe: {salida.receiver_user_name}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openReturnModal(salida)}
                        className="text-primary border-primary/30 hover:bg-primary/10"
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Registrar Retorno
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDismissSalida(salida.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                        title="Ocultar esta salida (préstamo a largo plazo)"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {visiblePendingSalidas.length > 8 && (
                  <p className="text-xs text-center text-muted-foreground pt-2">
                    Y {visiblePendingSalidas.length - 8} más...
                  </p>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Bulk Delete Action Bar */}
      {isAdmin && isSomeSelected && (
        <div className="flex items-center justify-between p-3 bg-destructive/10 border border-destructive/30 rounded-lg animate-fade-in" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">
            {selectedMovements.size} movimiento(s) seleccionado(s)
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelectedMovements(new Set())} data-testid="bulk-deselect-btn">
              Deseleccionar
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setIsBulkDeleteModalOpen(true)} data-testid="bulk-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar seleccionados
            </Button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por responsable, evento, cliente, equipo, serial o notas de transición..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-10"
          data-testid="movement-search"
        />
        {searchTerm && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            onClick={() => setSearchTerm('')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Movements Table */}
      <Card className="card-hover">
        <CardContent className="p-0 overflow-x-auto">
          <div className="overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow className="table-header">
                {isAdmin && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-movements"
                      aria-label="Seleccionar todos"
                    />
                  </TableHead>
                )}
                <TableHead className="whitespace-nowrap">Fecha</TableHead>
                <TableHead className="whitespace-nowrap">Tipo</TableHead>
                <TableHead className="whitespace-nowrap">No. Control</TableHead>
                <TableHead className="whitespace-nowrap max-w-[150px]">Evento</TableHead>
                <TableHead className="whitespace-nowrap">Fecha Evento</TableHead>
                <TableHead className="whitespace-nowrap">Responsable</TableHead>
                <TableHead className="whitespace-nowrap">Items</TableHead>
                <TableHead className="whitespace-nowrap">Autorizó</TableHead>
                <TableHead className="whitespace-nowrap">Estado</TableHead>
                <TableHead className="w-24 whitespace-nowrap">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={isAdmin ? 11 : 10}>
                      <Skeleton className="h-12 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredMovements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 11 : 10} className="text-center py-12">
                    <ArrowLeftRight className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">
                      {searchTerm ? 'No se encontraron movimientos' : 'No hay movimientos'}
                    </p>
                    {searchTerm && (
                      <Button variant="link" onClick={() => setSearchTerm('')} className="mt-2">
                        Limpiar búsqueda
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredMovements.map((mov) => (
                  <TableRow key={mov.id} className="table-row" data-testid={`movement-row-${mov.id}`}>
                    {isAdmin && (
                      <TableCell>
                        <Checkbox
                          checked={selectedMovements.has(mov.id)}
                          onCheckedChange={() => toggleSelectMovement(mov.id)}
                          data-testid={`select-movement-${mov.id}`}
                          aria-label={`Seleccionar movimiento ${mov.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-sm whitespace-nowrap">{formatShortDate(mov.created_at || mov.date)}</TableCell>
                    <TableCell>
                      <Badge className={mov.type === 'prestamo' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : getMovementTypeColor(mov.type)}>
                        {mov.type === 'prestamo' ? 'Préstamo' : getMovementTypeLabel(mov.type)}
                      </Badge>
                      {mov.include_qr && (
                        <Badge
                          className={`mt-1 ml-0 block w-fit text-[10px] ${
                            (mov.signatures?.length || 0) > 0
                              ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                              : 'bg-sky-500/20 text-sky-400 border-sky-500/30'
                          }`}
                          data-testid={`signature-badge-${mov.id}`}
                          title={
                            (mov.signatures?.length || 0) > 0
                              ? `Firmado digitalmente por ${mov.signatures[mov.signatures.length - 1]?.signer_name || 'cliente'}`
                              : 'QR de firma digital generado, pendiente de firma'
                          }
                        >
                          {(mov.signatures?.length || 0) > 0 ? '✓ Firmado' : 'QR pendiente'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {mov.control_number || '-'}
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      {mov.type === 'prestamo' ? (
                        <>
                          <span className="truncate block font-medium text-purple-400" title={mov.loan_reason}>
                            {mov.loan_reason || 'Préstamo'}
                          </span>
                          {mov.loan_duration_unit === 'indefinite' ? (
                            <span className="text-xs text-muted-foreground">Sin tiempo determinado</span>
                          ) : mov.loan_due_date && (
                            <span className={`text-xs ${new Date(mov.loan_due_date) < new Date() ? 'text-red-400' : 'text-muted-foreground'}`}>
                              Vence: {formatDate(mov.loan_due_date)}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="truncate block font-medium" title={mov.event_name}>
                            {mov.event_name || '-'}
                          </span>
                          {mov.client_name && (
                            <span className="text-xs text-muted-foreground truncate block">
                              {mov.client_name}
                            </span>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {mov.event_date ? (
                        <div>
                          <span>{formatDate(mov.event_date)}</span>
                          {mov.event_time && (
                            <span className="text-muted-foreground ml-1">{mov.event_time}</span>
                          )}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span data-testid={`mov-delivered-by-${mov.id}`}>
                          <span className="text-xs text-muted-foreground">Entregó: </span>{mov.responsible_name}
                        </span>
                        {(mov.receipt_info?.user_name || mov.receiver_user_name) && (
                          <span className="text-xs text-muted-foreground" data-testid={`mov-received-by-${mov.id}`}>
                            Recibió del almacén: {mov.receipt_info?.user_name || mov.receiver_user_name}
                          </span>
                        )}
                        {mov.delivery_info?.user_name && (
                          <span className="text-xs text-muted-foreground" data-testid={`mov-delivered-event-${mov.id}`}>
                            Entregó al evento: {mov.delivery_info.user_name}
                          </span>
                        )}
                        {mov.return_info?.user_name && (
                          <span className="text-xs text-muted-foreground" data-testid={`mov-received-event-${mov.id}`}>
                            Recibió del evento: {mov.return_info.user_name}
                          </span>
                        )}
                        {(mov.return_receipt_info?.user_name || mov.return_receiver_name) && (
                          <span className="text-xs text-muted-foreground" data-testid={`mov-returned-by-${mov.id}`}>
                            Entregó al almacén: {mov.return_receipt_info?.user_name || mov.return_receiver_name}
                            {!mov.return_receipt_info && mov.return_receiver_name && (
                              <span className="text-amber-500"> (pendiente)</span>
                            )}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span>{mov.items?.length || 0} equipos</span>
                        {mov.equipment_no_review && (
                          <span title="Salió sin revisión (urgencia)" className="text-red-400">
                            <AlertTriangle className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {mov.admin_override && mov.override_by_name ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Lock className="h-4 w-4 text-amber-500" />
                          <span className="truncate max-w-[80px] text-amber-400" title={`Override por ${mov.override_by_name}`}>
                            {mov.override_by_name}
                          </span>
                        </div>
                      ) : mov.verified_by_name ? (
                        <div className="flex items-center gap-1 text-sm">
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                          <span className="truncate max-w-[80px]" title={mov.verified_by_name}>
                            {mov.verified_by_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {mov.type === 'salida' && (
                        (() => {
                          const ws = normalizeStatus(mov.status || mov.workflow_status) || (mov.has_return ? 'completado' : 'salida');
                          const cls = WORKFLOW_BADGE_STYLES[ws] || WORKFLOW_BADGE_STYLES.salida;
                          const lbl = WORKFLOW_BADGE_LABELS[ws] || ws;
                          return (
                            <Badge className={cls} data-testid={`workflow-badge-${mov.id}`}>
                              {lbl}
                            </Badge>
                          );
                        })()
                      )}
                      {mov.type === 'prestamo' && (
                        mov.has_return ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400">Devuelto</Badge>
                        ) : mov.loan_due_date && new Date(mov.loan_due_date) < new Date() ? (
                          <Badge className="bg-red-500/20 text-red-400">Vencido</Badge>
                        ) : (
                          <Badge className="bg-purple-500/20 text-purple-400">En Préstamo</Badge>
                        )
                      )}
                      {mov.type === 'retorno' && (
                        <Badge className="bg-blue-500/20 text-blue-400">Completado</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openViewModal(mov)} title="Ver detalle">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDownloadPDF(mov.id)}
                          title="Descargar Responsiva PDF"
                          className="text-primary"
                        >
                          <FileDown className="h-4 w-4" />
                        </Button>
                        {/* Legacy: prestamo flow — keep original logic (no workflow) */}
                        {mov.type === 'prestamo' && !mov.has_return && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openReturnModal(mov)}
                            title="Registrar retorno"
                            className="text-primary"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                        {/* New 4-step workflow buttons (only for type=salida) */}
                        {mov.type === 'salida' && normalizeStatus(mov.status || mov.workflow_status) === 'salida' && (
                          (mov.receiver_user_id && !mov.receipt_info) ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setWorkflowMovement(mov); setWorkflowAction('receipt'); setWorkflowModalOpen(true); }}
                              title={`Paso 1: confirmar recibo (${mov.receiver_user_name || 'quien recibe'})`}
                              className="text-emerald-600"
                              data-testid={`workflow-receipt-btn-${mov.id}`}
                            >
                              <PackageCheck className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setWorkflowMovement(mov); setWorkflowAction('deliver'); setWorkflowModalOpen(true); }}
                              title="Paso 2: marcar como entregado"
                              className="text-blue-500"
                              data-testid={`workflow-deliver-btn-${mov.id}`}
                            >
                              <Truck className="h-4 w-4" />
                            </Button>
                          )
                        )}
                        {mov.type === 'salida' && normalizeStatus(mov.status || mov.workflow_status) === 'entregado' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setWorkflowMovement(mov); setWorkflowAction('return'); setWorkflowModalOpen(true); }}
                            title="Paso 3: marcar retorno (recibimos el equipo del evento)"
                            className="text-amber-500"
                            data-testid={`workflow-return-btn-${mov.id}`}
                          >
                            <PackageOpen className="h-4 w-4" />
                          </Button>
                        )}
                        {/* Paso 4: 'Confirmar entrega al almacén' — quien entrega a bodega se
                            auto-confirma con su usuario y contraseña. No completa aún. */}
                        {mov.type === 'salida' && normalizeStatus(mov.status || mov.workflow_status) === 'retorno' && !mov.return_receipt_info && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setWorkflowMovement(mov); setWorkflowAction('return_receipt'); setWorkflowModalOpen(true); }}
                            title="Paso 4: confirmar entrega al almacén (quien la entrega pone su usuario y contraseña)"
                            className="text-emerald-600"
                            data-testid={`workflow-return-confirm-btn-${mov.id}`}
                          >
                            <PackageCheck className="h-4 w-4" />
                          </Button>
                        )}
                        {/* Paso 5 (final): 'Registrar retorno' abre el ReturnModal (condiciones +
                            contraseña del responsable). Muestra automáticamente quién entregó al
                            almacén (paso 4). Completa el movimiento. */}
                        {mov.type === 'salida' && normalizeStatus(mov.status || mov.workflow_status) === 'retorno' && mov.return_receipt_info && !mov.has_return && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openReturnModal(mov)}
                            title="Paso 5: registrar retorno (final)"
                            className="text-emerald-500"
                            data-testid={`workflow-complete-btn-${mov.id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                        {hasPermission('duplicate_movements') && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openDuplicateModal(mov)}
                          title="Duplicar movimiento"
                          className="text-amber-500 hover:text-amber-400"
                          data-testid={`duplicate-btn-${mov.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        )}
                        {mov.is_duplicate && (
                          <Badge variant="outline" className="text-[9px] px-1 border-amber-500/30 text-amber-500">DUP</Badge>
                        )}
                        {isAdmin && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openEditModal(mov)}
                              title="Editar notas"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openDeleteModal(mov)}
                              className="text-destructive hover:text-destructive"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Salida Modal */}
      <CreateMovementModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        form={form}
        setForm={setForm}
        newItem={newItem}
        setNewItem={setNewItem}
        saving={saving}
        handleSave={handleSave}
        addItem={addItem}
        removeItem={removeItem}
        availableAtLocation={availableAtLocation}
        loadAvailableEquipment={loadAvailableEquipment}
        filteredAvailableEquipment={filteredAvailableEquipment}
        categoriesWithAvailability={categoriesWithAvailability}
        reservedUnitSerials={reservedUnitSerials}
        showResponsiblePassword={showResponsiblePassword}
        setShowResponsiblePassword={setShowResponsiblePassword}
        showAdminPassword={showAdminPassword}
        setShowAdminPassword={setShowAdminPassword}
        responsibles={responsibles}
        systemUsers={systemUsers}
        clients={clients}
        locations={locations}
        categories={categories}
        equipment={equipment}
        locationsWithAvailability={locationsWithAvailability}
        showScanner={showScanner}
        setShowScanner={setShowScanner}
        scannerConfig={scannerConfig}
        scanMode={scanMode}
        setScanMode={setScanMode}
        scanBatchMode={scanBatchMode}
        setScanBatchMode={setScanBatchMode}
        handleScanSuccess={handleScanSuccess}
        hasPermission={hasPermission}
        duplicateInfo={duplicateInfo}
        searchBySerial={searchBySerial}
        setSearchBySerial={setSearchBySerial}
        searchByReceptor={searchByReceptor}
        setSearchByReceptor={setSearchByReceptor}
      />

      {/* Return Modal */}
      <ReturnModal
        isOpen={isReturnModalOpen}
        setIsOpen={setIsReturnModalOpen}
        selectedSalida={selectedSalida}
        form={form}
        setForm={setForm}
        saving={saving}
        handleSave={handleSave}
        updateReturnItem={updateReturnItem}
        formatDate={formatDate}
        systemUsers={systemUsers}
        showResponsiblePassword={showResponsiblePassword}
        setShowResponsiblePassword={setShowResponsiblePassword}
        showAdminPassword={showAdminPassword}
        setShowAdminPassword={setShowAdminPassword}
      />

      {/* View Modal */}
      <MovementViewModal
        open={isViewModalOpen}
        onOpenChange={setIsViewModalOpen}
        movement={selectedMovement}
        onFetchReminderRecipients={async (mov) => {
          try {
            const res = await api().get(`/movements/${mov.id}/reminder-recipients`);
            return res?.data;
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo cargar la lista de destinatarios');
            return null;
          }
        }}
        onResendReminder={async (mov, target, selectedEmails) => {
          try {
            const config = target === 'directors' && Array.isArray(selectedEmails) && selectedEmails.length > 0
              ? { recipient_emails: selectedEmails }
              : {};
            const res = await api().post(`/movements/${mov.id}/resend-return-reminder?target=${target}`, config);
            const data = res?.data || {};
            if (data.sent) {
              const count = data.recipients?.length || 1;
              toast.success(
                target === 'creator'
                  ? 'Correo al creador enviado'
                  : `Correo enviado a ${count} destinatario(s)`
              );
              // Optimistically update the selected movement with the fresh timestamp
              setSelectedMovement((prev) => prev && prev.id === mov.id ? {
                ...prev,
                ...(target === 'creator' ? {
                  return_reminder_creator_sent: true,
                  return_reminder_creator_sent_at: data.sent_at,
                } : {
                  return_reminder_directors_sent: true,
                  return_reminder_directors_sent_at: data.sent_at,
                }),
              } : prev);
              // Refresh list so the next open reflects the change
              loadData();
            } else {
              toast.error(data.error || 'No se pudo enviar el correo');
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al reenviar el recordatorio');
          }
        }}
      />

      {/* Workflow Transition Modal (Salida -> Entregado -> Retorno -> Completado) */}
      <WorkflowTransitionModal
        open={workflowModalOpen}
        onOpenChange={setWorkflowModalOpen}
        action={workflowAction}
        movement={workflowMovement}
        responsibles={responsibles}
        api={api()}
        onSuccess={handleModalSuccess}
      />

      {/* Fase 2 — Solicitar confirmación de retorno (entrega al almacén) */}
      <RequestReturnConfirmModal
        open={returnReqOpen}
        onClose={() => { setReturnReqOpen(false); setReturnReqMovement(null); }}
        movement={returnReqMovement}
        systemUsers={systemUsers}
        onSubmit={submitReturnRequest}
      />

      {/* Delete Modal */}
      <MovementDeleteModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        movement={selectedMovement}
        api={api}
        onSuccess={handleModalSuccess}
      />

      {/* Edit Modal */}
      <MovementEditModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        movement={selectedMovement}
        api={api}
        onSuccess={handleModalSuccess}
      />

      {/* Emergency Mode Modal */}
      <EmergencyModeModal
        isOpen={showEmergencyModal}
        setIsOpen={setShowEmergencyModal}
        emergencyForm={emergencyForm}
        setEmergencyForm={setEmergencyForm}
        emergencyAvailableEquipment={emergencyAvailableEquipment}
        setEmergencyAvailableEquipment={setEmergencyAvailableEquipment}
        emergencyCategoriesWithAvailability={emergencyCategoriesWithAvailability}
        setEmergencyCategoriesWithAvailability={setEmergencyCategoriesWithAvailability}
        emergencyLoading={emergencyLoading}
        setEmergencyLoading={setEmergencyLoading}
        responsibles={responsibles}
        clients={clients}
        locationsWithAvailability={locationsWithAvailability}
        locations={locations}
        categories={categories}
        equipment={equipment}
        api={api}
        loadData={loadData}
        searchBySerial={searchBySerial}
        setSearchBySerial={setSearchBySerial}
        searchByReceptor={searchByReceptor}
        setSearchByReceptor={setSearchByReceptor}
      />

      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScanSuccess={handleScanSuccess}
        onBatchScanSuccess={handleBatchScanSuccess}
        scanMode={scanMode}
        showHistory={scannerConfig.show_equipment_history}
        autoAdd={scannerConfig.auto_add_on_scan}
        batchMode={scanBatchMode}
      />

      {/* Bulk Delete Modal */}
      <Dialog open={isBulkDeleteModalOpen} onOpenChange={(open) => {
        setIsBulkDeleteModalOpen(open);
        if (!open) setBulkDeleteCreds({ admin_username: '', admin_password: '' });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Eliminar {selectedMovements.size} Movimiento(s)
            </DialogTitle>
            <DialogDescription>
              Esta accion eliminara {selectedMovements.size} movimiento(s) seleccionado(s) y revertira los cambios de stock. Se requiere autorizacion de administrador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Usuario Administrador</Label>
              <Input
                value={bulkDeleteCreds.admin_username}
                onChange={(e) => setBulkDeleteCreds({ ...bulkDeleteCreds, admin_username: e.target.value })}
                placeholder="Usuario admin"
                data-testid="bulk-delete-username"
              />
            </div>
            <div className="space-y-2">
              <Label>Contrasena</Label>
              <PasswordInput
                value={bulkDeleteCreds.admin_password}
                onChange={(e) => setBulkDeleteCreds({ ...bulkDeleteCreds, admin_password: e.target.value })}
                placeholder="Contrasena"
                data-testid="bulk-delete-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="confirm-bulk-delete-btn">
              {bulkDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar {selectedMovements.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Admin Auth Dialog */}
      <Dialog open={showDuplicateAuth} onOpenChange={setShowDuplicateAuth}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-500" />
              Autorizacion Requerida
            </DialogTitle>
            <DialogDescription>
              Ingresa las credenciales de un administrador para duplicar este movimiento
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Usuario admin</Label>
              <Input
                value={duplicateAuthCreds.username}
                onChange={(e) => setDuplicateAuthCreds(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Usuario admin/superadmin"
                data-testid="duplicate-auth-username"
              />
            </div>
            <div>
              <Label>Contrasena</Label>
              <PasswordInput
                value={duplicateAuthCreds.password}
                onChange={(e) => setDuplicateAuthCreds(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Contrasena"
                onKeyDown={(e) => { if (e.key === 'Enter') handleDuplicateAuthConfirm(); }}
                data-testid="duplicate-auth-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateAuth(false)}>Cancelar</Button>
            <Button onClick={handleDuplicateAuthConfirm} disabled={!duplicateAuthCreds.username || !duplicateAuthCreds.password} data-testid="duplicate-auth-confirm">
              Autorizar y Duplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MovementsPage;
